import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle, XCircle, Slash, Pocket, Calendar, User, Briefcase , Printer, RotateCcw } from "react-feather";
import toast from "react-hot-toast";
import {
  useGetPettyCashVoucherQuery,
  useApprovePettyCashVoucherMutation,
  useRejectPettyCashVoucherMutation,
  useCancelPettyCashVoucherMutation,
  usePayPettyCashVoucherMutation,
  useGetPettyCashCategoriesQuery,
  useListPettyCashVoucherReturnsQuery,
  useRecordPettyCashVoucherReturnMutation,
} from "../../pettyCashApi";
import StatusBadge from "../../../../components/common/StatusBadge";
import ActorName from "../../../../components/common/ActorName";
import VoucherPrintModal from "../components/VoucherPrintModal";
import ReasonPrompt from "../../../../components/common/ReasonPrompt";
import {
  PETTY_CASH_VOUCHER_STATUS_CLASS,
  PETTY_CASH_VOUCHER_STATUS_LABEL,
  PETTY_CASH_RECEIPT_STATUS_CLASS,
  PETTY_CASH_RECEIPT_STATUS_LABEL,
} from "../../pettyCashStatusMaps";
import ReceiptUpload from "../../components/ReceiptUpload";
import VoucherAttachments, {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_KINDS,
  rejectBadAttachmentFiles,
  useUploadVoucherAttachments,
} from "../../components/VoucherAttachments";
import EmployeePicker from "../../../users/components/EmployeePicker";
import {
  useCanApproveVoucher, useCanPayVoucher,
  useCanSelfApprove, useSelfPayCeiling, useCanCancelAnyVoucher, useCanRecordReturn,
} from "../../usePettyCashPermissions";
import { useClaims } from "../../../auth/useClaims";
import { paths } from "../../../../routes/paths";

const money = (n) => (n == null ? "-" : Number(n).toFixed(2));

// Status badge/action-bar/inline-ReasonPrompt structure mirrors
// LpoDetail.jsx's pattern exactly - the same shape works for a workflow
// record here, just with Petty Cash's own status vocab and a Pay section
// (Category + Receipt Number + a real EmployeePicker, replacing Flutter's
// free-text-only "search staff" gap) instead of LPO's Stock-In button.
const VoucherDetail = () => {
  const { recordGuid } = useParams();
  const { userId } = useClaims();
  const canApprove = useCanApproveVoucher();
  const canPay = useCanPayVoucher();
  const canSelfApprove = useCanSelfApprove();
  const canCancelAny = useCanCancelAnyVoucher();
  const selfPayCeiling = useSelfPayCeiling();
  const canRecordReturn = useCanRecordReturn();

  const { data: voucher, isLoading, isError, error } = useGetPettyCashVoucherQuery(recordGuid);
  const { data: categories = [] } = useGetPettyCashCategoriesQuery();
  // Only a PAID voucher can have returns, so don't ask for the others.
  const { data: returns = [] } = useListPettyCashVoucherReturnsQuery(recordGuid, { skip: voucher?.status !== "PAID" });
  const [approveVoucher] = useApprovePettyCashVoucherMutation();
  const [rejectVoucher] = useRejectPettyCashVoucherMutation();
  const [cancelVoucher] = useCancelPettyCashVoucherMutation();
  const [payVoucher, { isLoading: isPaying }] = usePayPettyCashVoucherMutation();
  const [recordReturn, { isLoading: isRecordingReturn }] = useRecordPettyCashVoucherReturnMutation();

  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnAmount, setReturnAmount] = useState("");
  const [returnRemarks, setReturnRemarks] = useState("");

  const [showRejectForm, setShowRejectForm] = useState(false);
  const [showPrint, setShowPrint] = useState(false);
  const [showPayForm, setShowPayForm] = useState(false);
  const [payCategoryId, setPayCategoryId] = useState("");
  const [payReceiptNumber, setPayReceiptNumber] = useState("");
  const [payReference, setPayReference] = useState("");
  const [payEmployee, setPayEmployee] = useState(null);
  const [payPersonName, setPayPersonName] = useState("");
  // Cheque image / other documents picked on the Pay panel (Batch198). Uploaded only
  // AFTER the payment succeeds, so a failed upload never leaves the voucher unpaid.
  const [payFiles, setPayFiles] = useState([]);
  const [payFilesKind, setPayFilesKind] = useState("CHEQUE");
  const { uploadAll: uploadPayFiles } = useUploadVoucherAttachments();

  if (isLoading) {
    return <div className="page-wrapper"><div className="content"><p>Loading...</p></div></div>;
  }
  if (isError || !voucher) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.pettyCashVouchers} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Vouchers
          </Link>
          <div className="alert alert-danger">Could not load this voucher ({error?.message || "server error"}).</div>
        </div>
      </div>
    );
  }

  // By user id - names are display text and two people can share one. The
  // server re-checks RequestedByUserId regardless; this only decides buttons.
  const isOwner = userId != null && voucher.requestedByUserId === userId;
  const isSubmitted = voucher.status === "SUBMITTED";
  const isApproved = voucher.status === "APPROVED";
  const canApproveOrRejectNow = isSubmitted && (isOwner ? canSelfApprove : canApprove);
  // Two routes, mirroring the server (Batch146): a requester withdrawing their
  // own still-submitted voucher needs nothing extra, while cancelling one that
  // is already APPROVED - or somebody else's - needs PettyCash.CancelVoucher.
  // PAID is terminal: undoing a payment is a refund/adjustment with real ledger
  // consequences, not a cancellation. Approval itself moves no money, so there
  // is nothing to unwind when cancelling before payment.
  const canCancelNow = (isSubmitted && isOwner) || (canCancelAny && (isSubmitted || isApproved));
  const selfPayEligible = isSubmitted && isOwner && selfPayCeiling != null && Number(voucher.requestedAmount) <= selfPayCeiling;
  const canPayNow = (isApproved && canPay) || selfPayEligible;
  // The receipt is attached by whoever pays, not necessarily the original
  // requester (per direct instruction, 2026-08-29) - gated on pay ability,
  // not ownership. Kept manageable after PAID too, for a late/corrected
  // attachment by the same payer-permission holder.
  // Since 2026-09-22 the requester may also attach (or replace) while the voucher is still
  // SUBMITTED - "put an option to attach something ... on voucher request".
  const canManageReceipt = canPayNow || (voucher.status === "PAID" && canPay) || (isSubmitted && isOwner);
  // Supporting documents (Batch198) may be added at ANY status - a cheque is often
  // scanned after the voucher is paid - by whoever can pay vouchers or raised this one.
  const canManageAttachments = canPay || isOwner;
  // Returns (Batch168): the unspent part of a paid voucher coming back into the
  // box. The voucher stays PAID; what it actually cost is paid - returned.
  const returnedAmount = Number(voucher.returnedAmount || 0);
  const netAmount = Number(voucher.paidAmount || 0) - returnedAmount;
  const returnableAmount = voucher.status === "PAID" ? Math.max(netAmount, 0) : 0;
  const canRecordReturnNow = canRecordReturn && returnableAmount > 0;

  const handleRecordReturn = async () => {
    const amount = Number(returnAmount);
    if (!(amount > 0)) return toast.error("Enter the amount being returned");
    if (amount > returnableAmount) return toast.error(`At most ${money(returnableAmount)} can be returned`);
    // Posts to the fund straight away and cannot be edited afterwards, same as
    // an adjustment - so say exactly where the money is going.
    if (!window.confirm(`Return ${money(amount)} to ${voucher.fundName || "the fund"} against ${voucher.voucherNumber}? This cannot be undone.`)) return;
    try {
      const result = await recordReturn({ recordGuid, returnAmount: amount, remarks: returnRemarks || undefined }).unwrap();
      toast.success(`Return ${result?.returnNumber || ""} recorded`);
      setShowReturnForm(false);
      setReturnAmount("");
      setReturnRemarks("");
    } catch (e) {
      toast.error(e?.message || "Could not record the return");
    }
  };

  const handleApprove = async () => {
    try {
      await approveVoucher({ recordGuid }).unwrap();
      toast.success("Voucher approved");
    } catch (e) {
      toast.error(e?.message || "Could not approve the voucher");
    }
  };

  const handleReject = async (rejectionReason) => {
    try {
      await rejectVoucher({ recordGuid, rejectionReason }).unwrap();
      toast.success("Voucher rejected");
      setShowRejectForm(false);
    } catch (e) {
      toast.error(e?.message || "Could not reject the voucher");
    }
  };

  const handleCancel = async () => {
    // Voiding an already-approved voucher is a bigger deal than withdrawing
    // your own unapproved one, so say which is happening.
    const prompt = isApproved
      ? `Cancel ${voucher.voucherNumber}? It has already been approved and will not be payable.`
      : "Cancel this voucher request?";
    if (!window.confirm(prompt)) return;
    try {
      await cancelVoucher(recordGuid).unwrap();
      toast.success("Voucher cancelled");
    } catch (e) {
      toast.error(e?.message || "Could not cancel the voucher");
    }
  };

  const handlePay = async () => {
    if (!payCategoryId && !voucher.categoryId) return toast.error("Choose a category");
    try {
      await payVoucher({
        recordGuid,
        categoryId: payCategoryId ? Number(payCategoryId) : undefined,
        receiptNumber: payReceiptNumber || undefined,
        paymentReference: payReference || undefined,
        paidToEmployeeRecordGuid: payEmployee?.value || undefined,
        paidToPersonName: payPersonName || undefined,
      }).unwrap();
      toast.success("Voucher paid");
    } catch (e) {
      toast.error(e?.message || "Could not pay the voucher");
      return;
    }
    if (payFiles.length > 0) {
      const { uploaded } = await uploadPayFiles(recordGuid, payFiles, payFilesKind);
      if (uploaded > 0) toast.success(`${uploaded} document${uploaded === 1 ? "" : "s"} attached`);
    }
    setPayFiles([]);
    setShowPayForm(false);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <Link to={paths.pettyCashVouchers} className="d-inline-flex align-items-center mb-2">
                <ArrowLeft size={16} className="me-1" /> Back to Vouchers
              </Link>
              <h4>
                {voucher.voucherNumber}{" "}
                <StatusBadge status={voucher.status} classMap={PETTY_CASH_VOUCHER_STATUS_CLASS} labelMap={PETTY_CASH_VOUCHER_STATUS_LABEL} />
                {voucher.receiptStatus && voucher.receiptStatus !== "NOT_REQUIRED" && (
                  <span className="ms-2">
                    <StatusBadge
                      status={voucher.receiptStatus}
                      classMap={PETTY_CASH_RECEIPT_STATUS_CLASS}
                      labelMap={PETTY_CASH_RECEIPT_STATUS_LABEL}
                    />
                  </span>
                )}
              </h4>
              <h6>{voucher.branchCode}</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2 flex-wrap">
            {/* Deliberately ungated: a submitted or approved voucher is the sheet someone
                carries to get a signature or collect the cash. The printout stamps the
                status, so an unpaid one cannot pass as proof of payment. */}
            <button
              type="button"
              className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
              onClick={() => setShowPrint(true)}
            >
              <Printer size={16} /> Print
            </button>
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-success d-inline-flex align-items-center gap-1" onClick={handleApprove}>
                <CheckCircle size={16} /> Approve
              </button>
            )}
            {canApproveOrRejectNow && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={() => setShowRejectForm((s) => !s)}>
                <XCircle size={16} /> Reject
              </button>
            )}
            {canPayNow && (
              <button type="button" className="btn btn-added d-inline-flex align-items-center gap-1" onClick={() => setShowPayForm((s) => !s)}>
                <Pocket size={16} /> Pay
              </button>
            )}
            {canRecordReturnNow && (
              <button
                type="button"
                className="btn btn-outline-success d-inline-flex align-items-center gap-1"
                onClick={() => setShowReturnForm((s) => !s)}
              >
                <RotateCcw size={16} /> Record Return
              </button>
            )}
            {canCancelNow && (
              <button type="button" className="btn btn-outline-danger d-inline-flex align-items-center gap-1" onClick={handleCancel}>
                <Slash size={16} /> Cancel
              </button>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body d-flex align-items-center gap-3">
            <span className="master-detail-avatar master-detail-avatar-lg">
              {(voucher.requestedByName || "?").trim().slice(0, 2).toUpperCase()}
            </span>
            <div className="flex-grow-1">
              <h5 className="mb-0">{voucher.requestedByName}</h5>
              <div className="d-flex flex-wrap gap-3 mt-2 text-muted small">
                <span className="d-inline-flex align-items-center"><Calendar size={14} className="me-1" />{new Date(voucher.expenseDate).toLocaleDateString()}</span>
                {voucher.categoryName && (
                  <span className="d-inline-flex align-items-center"><Briefcase size={14} className="me-1" />{voucher.categoryName}</span>
                )}
                {(voucher.paidToEmployeeName || voucher.paidToPersonName) && (
                  <span className="d-inline-flex align-items-center"><User size={14} className="me-1" />Paid to {voucher.paidToEmployeeName || voucher.paidToPersonName}</span>
                )}
              </div>
            </div>

            {/* The amount is the one figure anyone opens a voucher to check, so it sits
                across from the name instead of buried in the Details grid below. Shows
                what was actually paid once it is paid, and what was asked for until then
                - labelled either way, so the two are never confused. */}
            <div className="text-end ps-3 border-start" style={{ minWidth: 170 }}>
              <div className="text-muted small d-inline-flex align-items-center mb-1">
                <Pocket size={14} className="me-1" />
                {voucher.paidAmount != null
                  ? "Paid amount"
                  : voucher.approvedAmount != null
                  ? "Approved amount"
                  : "Requested amount"}
              </div>
              <div className="fs-4 fw-bold text-nowrap">
                {money(voucher.paidAmount ?? voucher.approvedAmount ?? voucher.requestedAmount)}
              </div>
              {voucher.paidAmount != null && Number(voucher.paidAmount) !== Number(voucher.requestedAmount) && (
                <div className="text-muted small text-nowrap">
                  {money(voucher.requestedAmount)} requested
                </div>
              )}
              {returnedAmount > 0 && (
                <>
                  <div className="text-success small text-nowrap">{money(returnedAmount)} returned</div>
                  <div className="small fw-semibold text-nowrap">Net spend {money(netAmount)}</div>
                </>
              )}
            </div>
          </div>
        </div>

        {showRejectForm && (
          <ReasonPrompt label="Rejection Reason" confirmLabel="Reject" onConfirm={handleReject} onCancel={() => setShowRejectForm(false)} />
        )}

        {showPayForm && (
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="mb-3">Pay Voucher</h5>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Category</label>
                  <select className="form-control" value={payCategoryId} onChange={(e) => setPayCategoryId(e.target.value)}>
                    <option value="">{voucher.categoryName || "--Select--"}</option>
                    {categories.map((c) => (
                      <option key={c.categoryId} value={c.categoryId}>{c.categoryName}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Receipt Number</label>
                  <input type="text" className="form-control" value={payReceiptNumber} onChange={(e) => setPayReceiptNumber(e.target.value)} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">Payment Reference</label>
                  <input type="text" className="form-control" value={payReference} onChange={(e) => setPayReference(e.target.value)} />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label">Paid To (staff member)</label>
                  <EmployeePicker value={payEmployee} onChange={setPayEmployee} />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label">or a name (not staff)</label>
                  <input type="text" className="form-control" value={payPersonName} onChange={(e) => setPayPersonName(e.target.value)} disabled={Boolean(payEmployee)} />
                </div>
              </div>
              <div className="mb-3">
                <ReceiptUpload
                  recordGuid={voucher.recordGuid}
                  hasReceipt={voucher.hasReceipt}
                  fileName={voucher.receiptFileName}
                  canManage={canManageReceipt}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Cheque image / other documents</label>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  <select className="form-control" style={{ width: 140 }} value={payFilesKind} onChange={(e) => setPayFilesKind(e.target.value)}>
                    {ATTACHMENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                  </select>
                  <input
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    className="form-control"
                    style={{ maxWidth: 340 }}
                    onChange={(e) => setPayFiles(rejectBadAttachmentFiles([...(e.target.files || [])]))}
                  />
                  {payFiles.length > 0 && <span className="small text-muted">{payFiles.length} file{payFiles.length === 1 ? "" : "s"} will be attached after payment</span>}
                </div>
                <small className="text-muted">Optional. JPEG, PNG or PDF, up to 1 MB each - several can be picked at once. More can be added any time from the Documents card below.</small>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-added" onClick={handlePay} disabled={isPaying}>Confirm Payment</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowPayForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {showReturnForm && canRecordReturnNow && (
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="mb-1">Record Return</h5>
              <p className="text-muted small mb-3">
                Unspent cash handed back against {voucher.voucherNumber}. It goes back to{" "}
                <strong>{voucher.fundName || "the fund it was paid from"}</strong>
                {voucher.categoryName ? <> under <strong>{voucher.categoryName}</strong></> : null}, and up to{" "}
                <strong>{money(returnableAmount)}</strong> can still be returned.
              </p>
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Amount returned</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    max={returnableAmount}
                    className="form-control"
                    value={returnAmount}
                    onChange={(e) => setReturnAmount(e.target.value)}
                    placeholder={money(returnableAmount)}
                  />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label">Remarks (optional)</label>
                  <input
                    type="text"
                    maxLength={300}
                    className="form-control"
                    value={returnRemarks}
                    onChange={(e) => setReturnRemarks(e.target.value)}
                    placeholder="e.g. change from fuel purchase"
                  />
                </div>
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-success" onClick={handleRecordReturn} disabled={isRecordingReturn}>
                  Confirm Return
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowReturnForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {returns.length > 0 && (
          <div className="card mb-3">
            <div className="card-body">
              <h5 className="mb-3">Returns</h5>
              <div className="table-responsive">
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Return No</th>
                      <th>Date</th>
                      <th>Remarks</th>
                      <th>Recorded by</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {returns.map((r) => (
                      <tr key={r.recordGuid}>
                        <td>{r.returnNumber}</td>
                        <td>{new Date(r.returnDate).toLocaleString()}</td>
                        <td style={{ whiteSpace: "normal" }}>{r.remarks || "-"}</td>
                        <td>{r.createdByName || "-"}</td>
                        <td className="text-end">{money(r.returnAmount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="fw-semibold">
                      <td colSpan={4}>Paid {money(voucher.paidAmount)} &minus; returned {money(returnedAmount)} = net spend</td>
                      <td className="text-end">{money(netAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}

        {voucher.status === "REJECTED" && voucher.rejectionReason && (
          <div className="alert alert-danger">Rejected by {voucher.rejectedByName}: {voucher.rejectionReason}</div>
        )}
        {voucher.status === "CANCELLED" && (
          <div className="alert alert-secondary">Cancelled by {voucher.cancelledByName}</div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="mb-3">Details</h5>
            <div className="row mb-3">
              <div className="col-md-4"><strong>Description:</strong> {voucher.description}</div>
              <div className="col-md-4"><strong>Supplier:</strong> {voucher.supplierName || "-"}</div>
              <div className="col-md-4"><strong>Receipt No:</strong> {voucher.receiptNumber || "-"}</div>
              {voucher.approvedAmount != null && <div className="col-md-4 mt-2"><strong>Approved Amount:</strong> {money(voucher.approvedAmount)}</div>}
              {voucher.paidAmount != null && <div className="col-md-4 mt-2"><strong>Paid Amount:</strong> {money(voucher.paidAmount)}</div>}
              {returnedAmount > 0 && <div className="col-md-4 mt-2"><strong>Returned:</strong> {money(returnedAmount)}</div>}
              {returnedAmount > 0 && <div className="col-md-4 mt-2"><strong>Net Spend:</strong> {money(netAmount)}</div>}
            </div>
            {!showPayForm && (
              <ReceiptUpload
                recordGuid={voucher.recordGuid}
                hasReceipt={voucher.hasReceipt}
                fileName={voucher.receiptFileName}
                canManage={canManageReceipt}
              />
            )}
            {/* One line per step, each bulleted. As a single hyphen-joined run it wrapped
                mid-name and mid-timestamp, which made the trail hard to read at a glance. */}
            <ul className="text-muted small mt-3 mb-0 list-unstyled">
              {[
                ["Submitted", voucher.requestedByName, voucher.createdDate],
                ["Approved", voucher.approvedByName, voucher.approvedDate],
                ["Rejected", voucher.rejectedByName, voucher.rejectedDate],
                ["Paid", voucher.paidByName, voucher.paidDate],
                ["Cancelled", voucher.cancelledByName, voucher.cancelledDate],
              ]
                .filter(([, name]) => name)
                .map(([action, name, date]) => (
                  <li key={action} className="d-flex align-items-baseline gap-2">
                    <span style={{ fontSize: "0.5rem", lineHeight: 1.9 }}>&#9679;</span>
                    <span>
                      {action} by <ActorName action={action} name={name} />
                      {date ? ` on ${new Date(date).toLocaleString()}` : ""}
                    </span>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        {/* Always shown, with image thumbnails inline - the cheque should be visible on
            opening the voucher, not behind a view button (2026-09-30). */}
        <VoucherAttachments recordGuid={voucher.recordGuid} canManage={canManageAttachments} />
      </div>

      <VoucherPrintModal show={showPrint} onClose={() => setShowPrint(false)} voucher={voucher} />
    </div>
  );
};

export default VoucherDetail;
